import * as React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Stack,
  Typography,
} from "@mui/material";

export type ExportMode = "graph" | "graph+data";

// Forced as per requirement
export type ExportFormat = "png";
export type DataFormat = "xlsx";

export type ChartExportSelection = {
  mode: ExportMode;
  format: ExportFormat;      // always "png"
  pngScale: 1 | 2 | 3;        // fixed to 2 internally
  dataFormat: DataFormat;     // always "xlsx"
};

type Props = {
  open: boolean;
  title?: string;
  hasSupportingData: boolean;
  onClose: () => void;

  /** kept for compatibility with existing callers, not used by this dialog */
  exportRows: any[];

  onConfirm: (selection: ChartExportSelection) => void;
};

const ChartExportDialog: React.FC<Props> = ({
  open,
  title = "Download chart",
  hasSupportingData,
  onClose,
  onConfirm,
}) => {
  const [mode, setMode] = React.useState<ExportMode>("graph");

  // forced defaults
  const pngScale: 1 | 2 | 3 = 2;

  React.useEffect(() => {
    if (!hasSupportingData) setMode("graph");
  }, [hasSupportingData]);

  const canConfirm = mode === "graph" || (mode === "graph+data" && hasSupportingData);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth sx={{ zIndex: 99999 }}>
      <DialogTitle>{title}</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl>
            <FormLabel>Content</FormLabel>
            <RadioGroup value={mode} onChange={(e) => setMode(e.target.value as ExportMode)}>
              <FormControlLabel value="graph" control={<Radio />} label="Graph only" />
              <FormControlLabel
                value="graph+data"
                control={<Radio />}
                label="Graph + supporting data"
                disabled={!hasSupportingData}
              />
            </RadioGroup>

            {!hasSupportingData && (
              <Typography variant="caption" sx={{ mt: 0.5 }}>
                Supporting data is not available for this chart.
              </Typography>
            )}
          </FormControl>

          <Typography variant="body2" sx={{ opacity: 0.75 }}>
            Download format: <b>PNG</b>
            {mode === "graph+data" && hasSupportingData ? (
              <>
                {" "}• Supporting data: <b>Excel (.xlsx)</b>
              </>
            ) : null}
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!canConfirm}
          sx={{ backgroundColor: "#4BB75F", "&:hover": { backgroundColor: "#3aa451" } }}
          onClick={() =>
            onConfirm({
              mode,
              format: "png",
              pngScale,
              dataFormat: "xlsx",
            })
          }
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChartExportDialog;